import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  useInteractions,
  useDismiss,
  FloatingPortal,
  autoUpdate,
} from "@floating-ui/react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import {
  BreadAccount,
  BreadTransaction,
  FirestoreDocCategory,
  FirestoreDocTag,
  Merchant,
  P,
  Predicate,
} from "breadcommon";
import { RyeSearchBar } from "../rye/RyeSearchBar";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { InstitutionsContext } from "../firebaseio/InstitutionsContext";
import { TagsContext } from "../firebaseio/TagsContext";
import { useMerchants } from "../derived/useMerchants";
import { TransactionsNavContext } from "./TransactionsNavTypes";
import { TransactionColumn } from "./TransactionColumn";
import { buildClasses } from "../utils/buildClasses";
import { MerchantLogo } from "../common/MerchantLogo";
import { InstitutionLogo } from "../common/InstitutionLogo";
import { TagDot } from "../tags_screen/TagDot";
import { RyeIcon } from "../rye/RyeIcon";

const MAX_PER_GROUP = 5;

type Suggestion =
  | { kind: "search"; text: string }
  | { kind: "category"; category: FirestoreDocCategory }
  | { kind: "account"; account: BreadAccount }
  | { kind: "merchant"; merchant: Merchant }
  | { kind: "tag"; tag: FirestoreDocTag };

export function TransactionsSearchBar(): JSX.Element {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const accounts = useContext(AccountsContext);
  const categories = useContext(CategoriesContext);
  const institutions = useContext(InstitutionsContext);
  const tags = useContext(TagsContext);
  const merchants = useMerchants();

  const [text, setText] = useState(transactionsNav.searchString);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const inputElRef = useRef<HTMLInputElement | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => setIsOpen(open),
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip(),
      shift(),
      floatingSize({
        apply({ rects }) {
          setMenuWidth(rects.reference.width);
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  const suggestions = useMemo<Suggestion[]>(() => {
    const trimmed = text.trim();
    if (trimmed === "") return [];

    const lower = trimmed.toLowerCase();
    const result: Suggestion[] = [{ kind: "search", text: trimmed }];

    // Match category names using P.SubstringCI shape conceptually
    const categoryField = P.Field<FirestoreDocCategory, "name">("name");
    const categoryMatcher = P.SubstringCI(categoryField, P.Literal(trimmed));
    Array.from(categories.values())
      .filter((c) => categoryMatcher.applyTo(c))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, MAX_PER_GROUP)
      .forEach((category) => result.push({ kind: "category", category }));

    // Match account names
    const accountField = P.Field<BreadAccount, "name">("name");
    const accountMatcher = P.SubstringCI(accountField, P.Literal(trimmed));
    Array.from(accounts.values())
      .filter((a) => accountMatcher.applyTo(a))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, MAX_PER_GROUP)
      .forEach((account) => result.push({ kind: "account", account }));

    // Match merchant names — use the merchant predicate field shape
    const merchantField = P.NestedField<BreadTransaction, "merchant", "name">(
      "merchant",
      "name",
    );
    const merchantMatcher = P.SubstringCI(merchantField, P.Literal(trimmed));
    Array.from(merchants.values())
      .filter((m) =>
        merchantMatcher.applyTo({ merchant: m } as BreadTransaction),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, MAX_PER_GROUP)
      .forEach((merchant) => result.push({ kind: "merchant", merchant }));

    // Match tag names
    Array.from(tags.values())
      .filter((t) => t.name.toLowerCase().includes(lower))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, MAX_PER_GROUP)
      .forEach((tag) => result.push({ kind: "tag", tag }));

    return result;
  }, [text, categories, accounts, merchants, tags]);

  function setColumnPredicate(
    column: TransactionColumn,
    predicate: Predicate | null,
  ) {
    setText("");
    setIsOpen(false);
    inputElRef.current?.blur();
    setTransactionsNav((prev) => ({
      ...prev,
      searchString: "",
      searchStringExactMatch: false,
      columnSettings: {
        ...prev.columnSettings,
        [column]: {
          ...prev.columnSettings[column],
          predicate,
        },
      },
    }));
  }

  function applySuggestion(suggestion: Suggestion) {
    switch (suggestion.kind) {
      case "search":
        setIsOpen(false);
        inputElRef.current?.blur();
        return;
      case "category": {
        const field = P.Field<BreadTransaction, "categoryId">("categoryId");
        setColumnPredicate(
          TransactionColumn.CATEGORY,
          P.Any(P.Equals(field, P.Literal(suggestion.category.id))),
        );
        return;
      }
      case "account": {
        const field = P.Field<BreadTransaction, "account_id">("account_id");
        setColumnPredicate(
          TransactionColumn.ACCOUNT,
          P.Any(P.Equals(field, P.Literal(suggestion.account.id))),
        );
        return;
      }
      case "merchant": {
        const field = P.NestedField<BreadTransaction, "merchant", "name">(
          "merchant",
          "name",
        );
        setColumnPredicate(
          TransactionColumn.MERCHANT,
          P.Any(P.EqualsCI(field, P.Literal(suggestion.merchant.name))),
        );
        return;
      }
      case "tag": {
        const field = P.Field<BreadTransaction, "tagIds">("tagIds");
        setColumnPredicate(
          TransactionColumn.TAGS,
          P.Any(P.ListContains(field, P.Literal<string>(suggestion.tag.id))),
        );
        return;
      }
    }
  }

  function handleSearch(newText: string, exactMatch: boolean) {
    setText(newText);
    setTransactionsNav((prev) => ({
      ...prev,
      searchString: newText,
      searchStringExactMatch: exactMatch,
    }));
    if (newText.trim() === "") {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setHighlightIndex(0);
    }
  }

  function moveHighlight(delta: number) {
    if (suggestions.length === 0) return;
    setIsOpen(true);
    setHighlightIndex((prev) => {
      const n = suggestions.length;
      return (((prev + delta) % n) + n) % n;
    });
  }

  function handleEnter(): boolean {
    if (!isOpen || suggestions.length === 0) return false;
    applySuggestion(suggestions[highlightIndex]);
    return true;
  }

  const isMenuVisible = isOpen && suggestions.length > 0;

  return (
    // The margin leaves room for the bar to widen on focus without
    // crowding the filter badges beside it.
    <div className={buildClasses("relative", "h-7", "w-96", "mr-2")}>
      <div
        className={buildClasses(
          "absolute",
          "top-1/2",
          "-translate-y-1/2",
          "left-0",
          "z-10",
        )}
      >
        <RyeSearchBar
          search={handleSearch}
          startingText={transactionsNav.searchString}
          startingExactMatch={transactionsNav.searchStringExactMatch}
          placeholderText="Search transactions"
          size="sm"
          width="xl"
          variant="outlined"
          onArrowDown={() => moveHighlight(1)}
          onArrowUp={() => moveHighlight(-1)}
          onEnterOverride={handleEnter}
          inputRef={(el) => {
            inputElRef.current = el;
            refs.setReference(el);
          }}
          connectedBelow={isOpen && suggestions.length > 0}
          growOnFocus={true}
          highlightWhenActive={true}
        />
      </div>
      {isMenuVisible && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, width: menuWidth }}
            {...getFloatingProps()}
            className={buildClasses("z-[2000000000]")}
          >
            <SuggestionsMenu
              suggestions={suggestions}
              highlightIndex={highlightIndex}
              setHighlightIndex={setHighlightIndex}
              onSelect={applySuggestion}
              institutions={institutions}
            />
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}

function SuggestionsMenu({
  suggestions,
  highlightIndex,
  setHighlightIndex,
  onSelect,
  institutions,
}: {
  suggestions: Suggestion[];
  highlightIndex: number;
  setHighlightIndex: (i: number) => void;
  onSelect: (s: Suggestion) => void;
  institutions: Map<string, import("breadcommon").BreadInstitution>;
}) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (highlightIndex !== null && itemRefs.current[highlightIndex]) {
      itemRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightIndex]);

  return (
    <div
      className={buildClasses(
        "bg-surface-50",
        "border-tiny",
        "border-t-0",
        "border-on-surface-200",
        "rounded-b-[18px]",
        "overflow-hidden",
        "shadow-lg",
      )}
    >
      <OverlayScrollbarsComponent
        options={{ scrollbars: { autoHide: "scroll" } }}
        className={buildClasses("max-h-48")}
      >
        <div>
          {suggestions.map((s, index) => (
            <SuggestionRow
              key={suggestionKey(s)}
              rowRef={(el) => (itemRefs.current[index] = el)}
              suggestion={s}
              isHighlighted={highlightIndex === index}
              onClick={() => onSelect(s)}
              institutions={institutions}
            />
          ))}
        </div>
      </OverlayScrollbarsComponent>
    </div>
  );
}

function suggestionKey(s: Suggestion): string {
  switch (s.kind) {
    case "search":
      return `search:${s.text}`;
    case "category":
      return `category:${s.category.id}`;
    case "account":
      return `account:${s.account.id}`;
    case "merchant":
      return `merchant:${s.merchant.name}`;
    case "tag":
      return `tag:${s.tag.id}`;
  }
}

function SuggestionRow({
  suggestion,
  isHighlighted,
  onClick,
  institutions,
  rowRef,
}: {
  suggestion: Suggestion;
  isHighlighted: boolean;
  onClick: () => void;
  institutions: Map<string, import("breadcommon").BreadInstitution>;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={rowRef}
      // mousedown so we apply before the input's onBlur dismisses the menu
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={buildClasses(
        "px-3",
        "h-9",
        "text-md",
        "cursor-pointer",
        "flex",
        "items-center",
        "gap-2",
        {
          if: isHighlighted,
          then: "bg-on-surface-100",
          else: buildClasses("bg-surface-50", "hover:bg-on-surface-50"),
        },
      )}
    >
      <SuggestionContent suggestion={suggestion} institutions={institutions} />
    </div>
  );
}

function SuggestionContent({
  suggestion,
  institutions,
}: {
  suggestion: Suggestion;
  institutions: Map<string, import("breadcommon").BreadInstitution>;
}): JSX.Element {
  switch (suggestion.kind) {
    case "search":
      return (
        <>
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-center",
              "flex-shrink-0",
              "w-5",
            )}
          >
            <RyeIcon name="search" size="sm" />
          </div>
          <span className={buildClasses("truncate")}>{suggestion.text}</span>
          <span
            className={buildClasses(
              "ml-auto",
              "text-xs",
              "text-on-surface-400",
            )}
          >
            Search everywhere
          </span>
        </>
      );
    case "category":
      return (
        <>
          <span className={buildClasses("flex-shrink-0", "w-5", "text-center")}>
            {suggestion.category.emoji}
          </span>
          <span className={buildClasses("truncate")}>
            {suggestion.category.name}
          </span>
          <span
            className={buildClasses(
              "ml-auto",
              "text-xs",
              "text-on-surface-400",
            )}
          >
            Category
          </span>
        </>
      );
    case "account": {
      const institution = suggestion.account.institution_id
        ? institutions.get(suggestion.account.institution_id)
        : null;
      return (
        <>
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-center",
              "flex-shrink-0",
              "w-5",
            )}
          >
            <InstitutionLogo institution={institution} size={18} />
          </div>
          <span className={buildClasses("truncate")}>
            {suggestion.account.name}
          </span>
          <span
            className={buildClasses(
              "ml-auto",
              "text-xs",
              "text-on-surface-400",
            )}
          >
            Account
          </span>
        </>
      );
    }
    case "merchant":
      return (
        <>
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-center",
              "flex-shrink-0",
              "w-5",
            )}
          >
            <MerchantLogo merchant={suggestion.merchant} size={18} />
          </div>
          <span className={buildClasses("truncate")}>
            {suggestion.merchant.name}
          </span>
          <span
            className={buildClasses(
              "ml-auto",
              "text-xs",
              "text-on-surface-400",
            )}
          >
            Merchant
          </span>
        </>
      );
    case "tag":
      return (
        <>
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-center",
              "flex-shrink-0",
              "w-5",
              "h-[18px]",
            )}
          >
            <TagDot colorId={suggestion.tag.color} size="sm" />
          </div>
          <span className={buildClasses("truncate")}>
            {suggestion.tag.name}
          </span>
          <span
            className={buildClasses(
              "ml-auto",
              "text-xs",
              "text-on-surface-400",
            )}
          >
            Tag
          </span>
        </>
      );
  }
}
